/**
 * What a module post says about the playlist it holds.
 *
 * A published module is two records, exactly as a capsule and a book are: the
 * post, which the feed carries, and the `app.bsky.feed.module` record it points
 * at, which the feed does not. So a card for the post is built from two sources
 * of different speed and *both* are optional:
 *
 *  - the **post record**, on the first paint, which carries the module's URI and
 *    the few facts the publisher copied onto it (how many capsules, how much work,
 *    which subject and level);
 *  - the **module record**, one request later, which is where the playlist itself
 *    lives — its items, its title, its description, and whether it is a draft.
 *
 * A module's card has one need the other cards do not: it draws the *capsules*,
 * and those are neither of the two records above. So the facts carry the playlist
 * itself — both addresses per place, in order — and it is the card's job to fetch
 * the posts they name; a card whose record has not arrived draws the post's own
 * facts and an empty row, rather than claiming a playlist it cannot see.
 *
 * The one structural judgement is `isModulePost`, which is true for a post the
 * publisher marked (`type: 'module'`) *or* one carrying the back-ref — because
 * the patch is a second write, and a post whose second write failed is still a
 * module. Getting that wrong is not cosmetic: a published playlist would show in
 * the feed as a bare line of text.
 */
import {type ModuleItem} from '#/lib/modulePlaylist'
import {moduleItemsOfValue} from '#/lib/moduleRecord'

/** The back-ref the publisher patches onto the post, as the feed hands it over. */
export type ModuleRef = {uri: string; cid?: string}

/** The module record, as the query that fetches it resolves it. */
export type ModuleCardRecord = {
  uri: string
  title?: string
  description?: string
  status?: 'draft' | 'published'
  /** The playlist, both addresses per place (see `ModuleItem`). */
  items?: ModuleItem[]
  level?: string
  subject?: string
  post?: {uri: string}
}

export type ModuleCardFacts = {
  /** The module record this post points at, if the post says. */
  moduleUri: string | null
  /** What to call the playlist: its own title, else the post's first line. */
  title: string
  /** The module's own words, or an empty string when it has none. */
  description: string
  /** How many capsules, or null when nothing has said — never a stand-in zero. */
  capsuleCount: number | null
  /** Every exercise in every capsule, or null when nothing has said. */
  coins: number | null
  /** The stored codes, `Level_A5` and `Maths`, or null when the module names none. */
  level: string | null
  subject: string | null
  /** The capsules to draw, in order. Empty until the record arrives. */
  items: ModuleItem[]
}

/** A post, as loosely as the feed hands it over — extra fields and all. */
type Postish = {
  record?: unknown
  module?: unknown
}

/**
 * The DID whose repo holds the module a post points at — its author.
 *
 * Asked of the *module's own* URI rather than of the post's author, for the reason
 * the book card asks it of the book's: the record lives in a repo, and the account
 * that holds it is the person who built the course. A post that named somebody
 * else's record (nothing stops it) would otherwise offer *your* studio on *their*
 * module.
 */
export function moduleCreatorOf(
  moduleUri: string | null | undefined,
): string | null {
  if (typeof moduleUri !== 'string' || !moduleUri.startsWith('at://'))
    return null
  const authority = moduleUri.slice('at://'.length).split('/')[0] ?? ''
  return authority.startsWith('did:') ? authority : null
}

/** Read the module back-ref off a post, from any of the places it may sit. */
export function moduleRefOf(post: Postish | undefined): ModuleRef | null {
  if (!post) return null
  const record = (post.record ?? {}) as Record<string, unknown>
  for (const candidate of [post.module, record.module]) {
    if (
      candidate &&
      typeof candidate === 'object' &&
      typeof (candidate as {uri?: unknown}).uri === 'string'
    ) {
      const ref = candidate as {uri: string; cid?: unknown}
      return {
        uri: ref.uri,
        cid: typeof ref.cid === 'string' ? ref.cid : undefined,
      }
    }
  }
  return null
}

/**
 * Whether this post is a module.
 *
 * True for a post the publisher marked and for one carrying the back-ref, and
 * false for everything else: this predicate is about the evidence on the post
 * itself and never guesses.
 */
export function isModulePost(post: Postish | undefined): boolean {
  if (!post) return false
  const record = (post.record ?? {}) as Record<string, unknown>
  return record.type === 'module' || !!moduleRefOf(post)
}

/** The first line of a post's own text — the title the creator typed. */
function firstLineOf(text: unknown): string {
  if (typeof text !== 'string') return ''
  const line = text.split('\n').find(part => part.trim().length > 0)
  return line?.trim() ?? ''
}

/** A count that is a count: whole, not negative, and not a string. */
function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? Math.floor(value)
    : null
}

/** A stored code, or null when nothing usable was said. */
function code(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

/**
 * The facts a post and its module record agree on.
 *
 * `module` is the record the caller fetched, when it has one; everything on it is
 * optional, because a module written before a field existed is still a module.
 *
 * The counts are `null` rather than `0` when nothing has said, deliberately: the
 * card is drawn twice — once from the post alone while the record is in flight,
 * and once from both — and "0 capsules" would be a statement about the playlist
 * rather than about the request. `items` is the one list, and it is empty until
 * the record arrives, which is what tells the carousel to draw its placeholder
 * rather than an empty row.
 */
export function moduleCardFacts({
  post,
  module,
}: {
  post: Postish | undefined
  module?: ModuleCardRecord | null
}): ModuleCardFacts {
  const record = (post?.record ?? {}) as Record<string, unknown>
  const ref = moduleRefOf(post)
  const items = Array.isArray(module?.items) ? module.items : null

  return {
    moduleUri: module?.uri ?? ref?.uri ?? null,
    title:
      (module?.title && module.title.trim()) ||
      firstLineOf(record.text) ||
      'Module',
    description: (module?.description ?? '').trim(),
    /*
     * The record's own list first, then the count the publisher copied onto the
     * post: the record is what the learner plays, so it wins wherever the two
     * disagree, and the post's number is what stands in for it on the first
     * paint — a card that showed no length at all until a request landed would
     * look like a playlist with nothing in it.
     */
    capsuleCount: (items ? items.length : null) ?? count(record.moduleSize),
    coins: count(record.moduleCoins),
    level: code(module?.level) ?? code(record.level),
    subject: code(module?.subject) ?? code(record.subject),
    items: moduleItemsOfValue(items),
  }
}
